import type { AppSchemasProgrammingSessionProgramPageSession } from "@classes-service-types/app-schemas-programming-session-program-page-session";
import type { EducationLevel } from "@classes-service-types/education-level.ts";
import type { LearnerType } from "@classes-service-types/learner-type.ts";
import type { ProfileInDbWithApplications } from "@profile-service-types/profile-in-db-with-applications";
import { computed, type MaybeRefOrGetter, toValue } from "vue";
import { useInitialOptionsData } from "@/components/RegistrationProcess/composables/useInitialOptionsData.ts";
import {
  CONTINUING_EDUCATION_LEARNER_TYPE_IDS,
  EDUCATION_LEVELS,
  HIGH_SCHOOL_LEARNER_TYPE_IDS,
  NOT_ENROLLED_LEARNER_TYPE_IDS,
  PROFESSIONAL_LEARNER_TYPE_IDS,
  UNDERGRADUATE_LEARNER_TYPE_IDS,
} from "@/constants";

/**
 * Controls which Learner Types should appear inside dropdown based on program session
 * @param profile
 * @param session
 * @param selectedLearnerTypeIdParam
 */
export const useLearnerTypes = (
  profile: MaybeRefOrGetter<ProfileInDbWithApplications>,
  session: MaybeRefOrGetter<AppSchemasProgrammingSessionProgramPageSession>,
  selectedLearnerTypeIdParam: MaybeRefOrGetter<string>,
) => {
  const learnerTypeFromProfile = computed(() => toValue(profile)?.learner_type_id);
  // The generated `education_levels` type (`EducationLevels`) regenerated as an empty interface that
  // lost its array shape; the API returns an array of `EducationLevel`. Cast to the real runtime shape.
  // Remove this cast once the OpenAPI spec types the field as `EducationLevel[]` again.
  const providedEducationLevels = computed<EducationLevel[]>(() =>
    structuredClone((toValue(session)?.education_levels as EducationLevel[] | undefined) ?? []),
  );
  const selectedLearnerTypeId = computed(() => toValue(selectedLearnerTypeIdParam));

  const { initialData } = useInitialOptionsData();
  // Computed, not a one-time read: `initialData` is Vue Query data and is undefined until
  // the request resolves, so a snapshot taken at setup left the dropdown permanently
  // empty whenever this mounted before the learner types arrived.
  const learnerTypes = computed<LearnerType[]>(() => initialData?.value?.learnerTypes ?? []);
  const LEARNER_TYPE_ID_MAP: Record<string, string[]> = {
    [EDUCATION_LEVELS.undergraduate]: UNDERGRADUATE_LEARNER_TYPE_IDS,
    [EDUCATION_LEVELS.high_school]: HIGH_SCHOOL_LEARNER_TYPE_IDS,
    [EDUCATION_LEVELS.not_enrolled]: NOT_ENROLLED_LEARNER_TYPE_IDS,
    [EDUCATION_LEVELS.continuing_education]: CONTINUING_EDUCATION_LEARNER_TYPE_IDS,
    [EDUCATION_LEVELS.professional]: PROFESSIONAL_LEARNER_TYPE_IDS,
  };

  const filteredLearnerTypes = computed(() => {
    if (providedEducationLevels.value.length < 1) {
      return learnerTypes.value;
    }

    const activeIdLists = providedEducationLevels.value
      .map((educationLevel) => (educationLevel.value ? LEARNER_TYPE_ID_MAP[educationLevel.value] : undefined))
      .filter((idList): idList is string[] => Boolean(idList));

    return learnerTypes.value.filter((type) =>
      activeIdLists.some((idList: string[]) => idList.some((id: string) => type.id.includes(id))),
    );
  });

  const currentLearnerTypeIsValid = computed(() => {
    return filteredLearnerTypes?.value.some((type) => type?.id === selectedLearnerTypeId.value);
  });

  const invalidLearnerTypeFromProfile = computed(() => {
    return (
      learnerTypeFromProfile.value &&
      !filteredLearnerTypes?.value.some((type) => type?.id === learnerTypeFromProfile.value)
    );
  });

  return { filteredLearnerTypes, currentLearnerTypeIsValid, invalidLearnerTypeFromProfile };
};
